import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-root-toast';

import { CHECKLIST_PURPOSE } from '../../../common/constants';
import {
	GET_ACTIVE_CHECKLIST_TEMPLATE,
	GET_MY_CHECKLIST_SUBMISSION_ANSWERS,
	SUBMIT_CHECKLIST,
} from '../../../common/graphql/checklist';
import {
	CHECKLIST_ITEM_TYPES,
	getFlaggedItemIds,
	getVisibleChecklistItems,
} from '../../../common/utils/checklist/checklistItems';
import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import { getUserName } from '../../../common/utils/user/getUserName';
import { useOfflineQueue } from '../../offline';
import useOfflineMutation from '../../offline/useOfflineMutation';
import {
	MAX_PHOTOS_PER_ITEM,
	getNumberValidationError,
	groupIntoSections,
	isAnswerProvided,
} from './checklistFormHelpers';

// Answers as the form keeps them ({ [itemId]: value } and { [itemId]: comment }).
const toFormAnswers = submittedAnswers => (submittedAnswers || []).reduce((form, answer) => {
	if (answer?.itemId) {
		form.answers[answer.itemId] = answer.value;
		if (answer.comment) {
			form.comments[answer.itemId] = answer.comment;
		}
	}
	return form;
}, { answers: {}, comments: {} });

/**
 * Checklist form state for ChecklistFormView: the active template for
 * `purpose`, answers and comments, validation, manager sign-off, photos from
 * the shared media picker (captureMedia / capturedMediaBatch of
 * withMediaPicker) and submission through the offline queue (goes back once
 * submitted or queued). A checklist already submitted today (or queued on the
 * phone) is returned as `submitted` ({ isQueued, answers, hasManagerSignOff }
 * while queued, else the saved submission with its sign-off fields) and cannot
 * be submitted again.
 */
const useChecklistForm = ({ purpose, serviceRunId, vehicleId, captureMedia, capturedMediaBatch }) => {
	const navigation = useNavigation();
	const { tenant } = useUserContext();
	const { queue, isOnline } = useOfflineQueue();
	const [answers, setAnswers] = useState({});
	const [comments, setComments] = useState({});
	const [validationErrors, setValidationErrors] = useState({});
	const [managerSignOff, setManagerSignOff] = useState(null);
	const [managerSignOffError, setManagerSignOffError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const activePhotoItemIdRef = useRef(null);
	const lastCapturedMediaBatchIdRef = useRef(0);

	const { data, loading, error, refetch } = useQuery(GET_ACTIVE_CHECKLIST_TEMPLATE, {
		variables: { purpose },
		fetchPolicy: 'cache-and-network',
		skip: !purpose,
	});
	const template = data?.getActiveChecklistTemplate;

	// The server keeps one submission per checklist, person and day (the same
	// date the submit sends). A submission already known on the phone (also from
	// the day screen's list, which lacks answers or sign-off) shows at once with
	// whatever answers are cached; otherwise the form waits for the server while
	// online, so a checklist submitted elsewhere never opens editable.
	const submissionDate = getLocalTime(tenant?.timezone).format('YYYY-MM-DD');
	const { data: submissionsData, loading: submissionsLoading } = useQuery(GET_MY_CHECKLIST_SUBMISSION_ANSWERS, {
		variables: { date: submissionDate },
		fetchPolicy: 'cache-and-network',
		returnPartialData: true,
		errorPolicy: 'all',
		skip: !purpose,
	});
	const submitted = useMemo(() => {
		const queuedEntry = (queue || []).find(entry => (
			entry.configKey === 'submitChecklist' &&
			Number(entry.meta?.purpose) === Number(purpose) &&
			entry.meta?.date === submissionDate
		));
		if (queuedEntry) {
			return { isQueued: true, answers: queuedEntry.variables?.answers, hasManagerSignOff: Boolean(queuedEntry.variables?.managerSignOffBy) };
		}

		const submission = (submissionsData?.getMyChecklistSubmissions || [])
			.find(item => Number(item?.purpose) === Number(purpose));
		return submission ? { ...submission, isQueued: false } : null;
	}, [purpose, queue, submissionDate, submissionsData?.getMyChecklistSubmissions]);
	const isCheckingSubmission = isOnline && submissionsLoading && !submitted;
	const submittedForm = useMemo(() => (submitted ? toFormAnswers(submitted.answers) : null), [submitted]);
	const shownAnswers = submittedForm ? submittedForm.answers : answers;
	const shownComments = submittedForm ? submittedForm.comments : comments;

	const items = useMemo(() => template?.items || [], [template?.items]);
	const visibleItems = useMemo(() => getVisibleChecklistItems(items, shownAnswers), [shownAnswers, items]);
	const sections = useMemo(() => groupIntoSections(visibleItems), [visibleItems]);
	const isVehicleCheck = Number(purpose) === CHECKLIST_PURPOSE.VEHICLE_END_OF_DAY;
	// Any template that requires manager approval asks for the manager, who then signs off on their phone.
	const requiresManagerSignOff = Boolean(template?.requiresManagerApproval);

	useEffect(() => {
		const visibleItemIds = new Set(visibleItems.map(item => item.id));
		setValidationErrors((previousErrors) => {
			const nextErrors = Object.fromEntries(Object.entries(previousErrors)
				.filter(([itemId]) => visibleItemIds.has(itemId)));
			return Object.keys(nextErrors).length === Object.keys(previousErrors).length
				? previousErrors
				: nextErrors;
		});
	}, [visibleItems]);

	const [submitChecklist] = useOfflineMutation(SUBMIT_CHECKLIST, {
		configKey: 'submitChecklist',
	});

	const setAnswerValue = useCallback((itemId, value) => {
		setAnswers(previousAnswers => ({
			...previousAnswers,
			[itemId]: value,
		}));
		setValidationErrors(previousErrors => {
			if (!previousErrors[itemId]) {
				return previousErrors;
			}
			const nextErrors = { ...previousErrors };
			delete nextErrors[itemId];
			return nextErrors;
		});
	}, []);

	const setCommentValue = useCallback((itemId, value) => {
		setComments(previousComments => ({
			...previousComments,
			[itemId]: value,
		}));
	}, []);

	const draftAnswersArray = useMemo(() => (
		Object.keys(shownAnswers).map(itemId => ({
			itemId,
			value: shownAnswers[itemId],
			comment: shownComments[itemId],
		}))
	), [shownAnswers, shownComments]);

	const flaggedItemIds = useMemo(() => (
		new Set(getFlaggedItemIds(items, draftAnswersArray))
	), [draftAnswersArray, items]);

	// Required items still unanswered: the submit button stays locked until this is empty.
	const missingRequiredItems = useMemo(() => visibleItems.filter(item => (
		item.type !== CHECKLIST_ITEM_TYPES.HEADING &&
		item.required &&
		!isAnswerProvided(item, shownAnswers[item.id])
	)), [shownAnswers, visibleItems]);
	const requiredCount = useMemo(() => visibleItems.filter(item => (
		item.type !== CHECKLIST_ITEM_TYPES.HEADING && item.required
	)).length, [visibleItems]);

	// Append photos uploaded via the shared media picker to the item that opened it.
	// A single selection also arrives as a one-link batch, so this is the only channel.
	useEffect(() => {
		if (!capturedMediaBatch || capturedMediaBatch.id === lastCapturedMediaBatchIdRef.current) {
			return;
		}

		lastCapturedMediaBatchIdRef.current = capturedMediaBatch.id;
		const photoItemId = activePhotoItemIdRef.current;
		if (!photoItemId) {
			return;
		}

		const batchLinks = Array.isArray(capturedMediaBatch.links) ? capturedMediaBatch.links : [];
		const currentLinks = Array.isArray(answers[photoItemId]) ? answers[photoItemId] : [];
		const newLinks = batchLinks.filter((link, index) => (
			link && batchLinks.indexOf(link) === index && !currentLinks.includes(link)
		));
		const remainingSlots = Math.max(MAX_PHOTOS_PER_ITEM - currentLinks.length, 0);
		const linksToAdd = newLinks.slice(0, remainingSlots);

		if (!linksToAdd.length) {
			if (newLinks.length) {
				Toast.show(`This item already has the maximum ${MAX_PHOTOS_PER_ITEM} photos`);
			}
			return;
		}

		setAnswers((previousAnswers) => {
			const links = Array.isArray(previousAnswers[photoItemId]) ? previousAnswers[photoItemId] : [];
			const linksForItem = linksToAdd.filter(link => !links.includes(link));
			return linksForItem.length
				? { ...previousAnswers, [photoItemId]: [...links, ...linksForItem] }
				: previousAnswers;
		});

		const droppedCount = newLinks.length - linksToAdd.length;
		if (droppedCount > 0) {
			Toast.show(`Added ${linksToAdd.length} of ${newLinks.length} photos. This item allows up to ${MAX_PHOTOS_PER_ITEM}.`);
		}
	}, [answers, capturedMediaBatch]);

	const startPhotoCapture = useCallback((itemId) => {
		const currentLinks = Array.isArray(answers[itemId]) ? answers[itemId] : [];
		const remainingSlots = MAX_PHOTOS_PER_ITEM - currentLinks.length;
		if (remainingSlots <= 0) {
			return;
		}
		activePhotoItemIdRef.current = itemId;
		captureMedia({ selectionLimit: remainingSlots });
	}, [answers, captureMedia]);

	const removePhoto = useCallback((itemId, link) => {
		setAnswers(previousAnswers => ({
			...previousAnswers,
			[itemId]: (previousAnswers[itemId] || []).filter(existingLink => existingLink !== link),
		}));
	}, []);

	const handleSubmit = useCallback(async () => {
		if (submitted) {
			return;
		}

		const errors = {};

		visibleItems.forEach((item) => {
			if (item.type === CHECKLIST_ITEM_TYPES.HEADING) {
				return;
			}

			if (item.required && !isAnswerProvided(item, answers[item.id])) {
				errors[item.id] = 'This item is required';
				return;
			}

			if (item.type === CHECKLIST_ITEM_TYPES.NUMBER) {
				const numberError = getNumberValidationError(item, answers[item.id]);
				if (numberError) {
					errors[item.id] = numberError;
				}
			}
		});

		const managerError = requiresManagerSignOff && !managerSignOff
			? 'Select the manager who will sign off'
			: '';
		setManagerSignOffError(managerError);

		if (Object.keys(errors).length || managerError) {
			setValidationErrors(errors);
			return;
		}

		const submittedAnswers = visibleItems
			.filter(item => item.type !== CHECKLIST_ITEM_TYPES.HEADING && answers[item.id] !== undefined)
			.map(item => ({
				itemId: item.id,
				value: item.type === CHECKLIST_ITEM_TYPES.NUMBER ? Number(answers[item.id]) : answers[item.id],
				comment: comments[item.id] || undefined,
			}));

		setIsSubmitting(true);
		try {
			const result = await submitChecklist({
				variables: {
					templateId: template.id,
					date: submissionDate,
					serviceRun: serviceRunId,
					vehicle: vehicleId,
					managerSignOffBy: managerSignOff?.id,
					answers: submittedAnswers,
				},
				context: {
					offlineMeta: {
						purpose,
						checklistTitle: template.title,
					},
				},
			});

			if (result?.offline) {
				Toast.show(`${isVehicleCheck ? 'Vehicle check' : 'Checklist'} queued — it will sync automatically`);
			}
			else {
				Toast.show(requiresManagerSignOff
					? `Sent to ${getUserName(managerSignOff)} to sign off`
					: 'Checklist submitted');
			}
			navigation.goBack();
		}
		catch (submitError) {
			Toast.show(submitError?.message || 'Unable to submit the checklist');
		}
		finally {
			setIsSubmitting(false);
		}
	}, [
		answers,
		comments,
		isVehicleCheck,
		managerSignOff,
		navigation,
		purpose,
		requiresManagerSignOff,
		serviceRunId,
		submissionDate,
		submitChecklist,
		submitted,
		template,
		vehicleId,
		visibleItems,
	]);
	return {
		template,
		loading,
		error,
		refetch,
		isCheckingSubmission,
		submitted,
		visibleItems,
		sections,
		isVehicleCheck,
		requiresManagerSignOff,
		answers: shownAnswers,
		comments: shownComments,
		validationErrors,
		flaggedItemIds,
		missingRequiredItems,
		requiredCount,
		managerSignOff,
		setManagerSignOff,
		managerSignOffError,
		setManagerSignOffError,
		isSubmitting,
		activePhotoItemIdRef,
		setAnswerValue,
		setCommentValue,
		startPhotoCapture,
		removePhoto,
		handleSubmit,
	};
};

export default useChecklistForm;
